import { useMemo } from 'react';

import { useOfferSavings } from '~src/hooks/useOfferSavings';
import { DeepPartial, Loan, LoanInstalment, OfferProducts, Residence } from '~src/model';
import {
  calculateOfferPrice,
  findJyskeBankLoanCharacteristics,
  findLoanAnnualExpenseRatio,
  findLoanCharacteristics,
  withVAT,
} from '~src/util';

import { useFetchNewOfferExpenses } from './graphql/useExpense';
import { useNewOfferPriceWithExpenses } from './useNewOfferPriceWithExpenses';

export type FinancingProducts = Pick<
  DeepPartial<OfferProducts>,
  'heatPumpIndoorUnit' | 'heatPumpOutdoorUnit' | 'solarPanel' | 'inverter' | 'battery' | 'solarPackageDiscount'
>;

type GenericFinancingParameters = {
  products?: FinancingProducts;
  residence?: DeepPartial<Residence>;
  loan: Loan;
  calculationMethod?: 'jyskebank' | 'nordea';
};

type Financing = ReturnType<typeof useGenericFinancing>;

export const useGenericFinancing = ({
  products,
  loan,
  residence,
  calculationMethod = 'nordea',
}: GenericFinancingParameters) => {
  const newOfferExpenses = useFetchNewOfferExpenses();

  const { saved } = useOfferSavings(products, residence) ?? {};
  const expensesPrice = useNewOfferPriceWithExpenses(products, newOfferExpenses);

  const price = withVAT(calculateOfferPrice(products) + expensesPrice - (products?.solarPackageDiscount ?? 0));

  const { price: savedPerYear } = saved ?? {};

  const financingInformation = useMemo(
    () =>
      calculationMethod === 'jyskebank'
        ? findJyskeBankLoanCharacteristics(price, loan)
        : findLoanCharacteristics(price, loan),
    [loan, price, calculationMethod]
  );

  return {
    ...financingInformation,
    price,
    savedPerYear,
  };
};

/**
 * This function takes a list of financing objects and sums the costs and interests,
 * then calculates a new annualExpenseRatio (ÅOP). It is used when an offer has multiple
 * product types (e.g. heatPump and solar) and the loans for these products differ.
 * In this case we still want to present the user with what looks like a single financing
 * option.
 */
export const getCombinedFinancing = (financingList: Financing[]): Financing | undefined => {
  if (financingList.length === 0) {
    return;
  }

  if (financingList.length === 1) {
    return financingList[0];
  }

  const combinedFinancing = financingList.reduce(
    (combined, financing, index) =>
      index === 0
        ? combined
        : {
            ...combined,
            instalments: combineInstalmentLists(combined.instalments, financing.instalments),
            paymentPerInstalment: combined.paymentPerInstalment + financing.paymentPerInstalment,
            paymentPerMonth: combined.paymentPerMonth + financing.paymentPerMonth,
            price: combined.price + financing.price,
            savedPerYear: (combined.savedPerYear ?? 0) + (financing.savedPerYear ?? 0),
            total: combined.total + financing.total,
            totalFinancingCost: combined.totalFinancingCost + financing.totalFinancingCost,
            totalInterest: combined.totalInterest + financing.totalInterest,
            totalPaid: combined.totalPaid + financing.totalPaid,
            totalCommission: combined.totalCommission + financing.totalCommission,
            financingCommission: combined.financingCommission + financing.financingCommission,
            financingFee: combined.financingFee + financing.financingFee,
            financingLandRegistry: combined.financingLandRegistry + financing.financingLandRegistry,
          },
    financingList[0]
  );

  combinedFinancing.annualExpenseRatio = findLoanAnnualExpenseRatio(
    combinedFinancing.total,
    combinedFinancing.totalFinancingCost,
    combinedFinancing.startDate,
    combinedFinancing.instalments
  );

  return combinedFinancing;
};

const combineInstalmentLists = (first: LoanInstalment[], second: LoanInstalment[]): LoanInstalment[] =>
  first.map((instalment, index) => combineInstalment(instalment, second[index]));

const combineInstalment = (first: LoanInstalment, second: LoanInstalment): LoanInstalment => ({
  date: first.date,
  decrease: first.decrease + second.decrease,
  interest: first.interest + second.interest,
  payment: first.payment + second.payment,
  remaining: first.remaining + second.remaining,
});
